import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import VideoPlayer from '../components/video/VideoPlayer';
import { useToast } from '../components/layout/ToastProvider';
import { supabase } from '../supabase/supabase';
import { motion, AnimatePresence } from 'framer-motion';

const { 
  FiChevronLeft, FiPlay, FiCheckCircle, FiFileText, FiHelpCircle, 
  FiArrowRight, FiCircle, FiDownload, FiAirplay, FiMic, FiMonitor, 
  FiFile, FiTarget, FiLock, FiExternalLink, FiMaximize2, FiShield 
} = FiIcons;

export default function CoursePlayer() {
  const { id } = useParams();
  const { addToast } = useToast();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [prereqsNotMet, setPrereqsNotMet] = useState([]);
  const [totalLessons, setTotalLessons] = useState(0);

  useEffect(() => { 
    fetchCourseDetails(); 
  }, [id]);

  const fetchCourseDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const [courseRes, moduleRes, progressRes, prereqRes] = await Promise.all([
        supabase.from('courses_2024').select('*').eq('id', id).single(),
        supabase.from('modules_2024').select(`*, lessons:lessons_2024(*)`).eq('course_id', id).order('order_index', { ascending: true }),
        supabase.from('lesson_progress_2024').select('lesson_id').eq('course_id', id).eq('user_id', user.id).eq('is_completed', true),
        supabase.from('course_prerequisites_2024').select('prerequisite_id, prerequisite:courses_2024(title)').eq('course_id', id)
      ]);
      
      // Check Prerequisites
      if (prereqRes.data?.length > 0) {
        const preIds = prereqRes.data.map(p => p.prerequisite_id);
        const { data: enrollments } = await supabase
          .from('enrollments_2024')
          .select('course_id, status')
          .in('course_id', preIds);
        
        const incomplete = prereqRes.data.filter(p => {
          const enrollment = enrollments?.find(e => e.course_id === p.prerequisite_id);
          return !enrollment || enrollment.status !== 'completed';
        });

        if (incomplete.length > 0) {
          setPrereqsNotMet(incomplete);
          setLoading(false);
          return;
        }
      }

      const allLessons = moduleRes.data?.flatMap(m => m.lessons || []) || [];
      setTotalLessons(allLessons.length);
      setCourse(courseRes.data);
      setModules(moduleRes.data || []);
      setCompletedLessons(new Set(progressRes.data?.map(p => p.lesson_id) || []));
      
      if (allLessons.length > 0) {
        setActiveItem({ ...allLessons[0], type: 'lesson' });
      }
    } catch (error) { 
      addToast(error.message, "error"); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleComplete = async () => {
    if (!activeItem || completedLessons.has(activeItem.id)) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // 1. Mark lesson as complete
      await supabase.from('lesson_progress_2024').upsert([{
        lesson_id: activeItem.id, course_id: id, user_id: user.id, is_completed: true
      }]);

      const newCompleted = new Set([...completedLessons, activeItem.id]);
      setCompletedLessons(newCompleted);

      // 2. Update overall course progress
      const progressPercent = Math.round((newCompleted.size / totalLessons) * 100);
      const isFinished = progressPercent === 100;

      await supabase.from('enrollments_2024')
        .update({ 
          progress_percent: progressPercent,
          status: isFinished ? 'completed' : 'active',
          completed_at: isFinished ? new Date() : null
        })
        .eq('course_id', id)
        .eq('client_id', (await supabase.from('clients_2024').select('id').eq('email', user.email).single()).data.id);

      addToast(isFinished ? "Congratulations! Course Completed!" : "Lesson progress saved.");
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  const renderStage = () => {
    if (!activeItem) return null;
    switch (activeItem.content_type) {
      case 'video': return <VideoPlayer url={activeItem.video_url} title={activeItem.title} onComplete={handleComplete} />;
      case 'text': return (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-12 min-h-[500px] shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-white mb-10">{activeItem.title}</h1>
            <div className="prose prose-invert text-slate-300 leading-relaxed text-lg space-y-6">
              {activeItem.body_content?.split('\n').map((para, i) => <p key={i}>{para}</p>) || "No content available."}
            </div>
          </div>
        </div>
      );
      default: return (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-20 text-center flex flex-col items-center justify-center min-h-[450px]">
          <div className="w-24 h-24 bg-indigo-500/10 rounded-3xl flex items-center justify-center mb-8">
            <SafeIcon icon={FiFile} className="text-5xl text-indigo-400" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">{activeItem.title}</h2>
          <p className="text-slate-400 mb-8">This resource is available for download or external viewing.</p>
          <a href={activeItem.file_url || activeItem.external_url} target="_blank" rel="noreferrer" className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-4 rounded-2xl font-bold transition-all flex items-center gap-2">
            View Resource <SafeIcon icon={FiExternalLink} />
          </a>
        </div>
      );
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>;

  if (prereqsNotMet.length > 0) {
    return (
      <div className="max-w-xl mx-auto py-32">
        <div className="bg-slate-900 border border-slate-800 rounded-[40px] p-12 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-rose-500 to-transparent opacity-50" />
          <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiLock} className="text-4xl text-rose-500" />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-4 tracking-tight">Prerequisites Required</h1>
          <p className="text-slate-400 mb-10 leading-relaxed">To ensure you have the foundational knowledge, please complete these courses first:</p>
          <div className="space-y-3">
            {prereqsNotMet.map(p => (
              <div key={p.prerequisite_id} className="bg-slate-800/50 border border-slate-700/50 p-5 rounded-2xl flex items-center justify-between group hover:bg-slate-800 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-700 flex items-center justify-center text-slate-400">
                    <SafeIcon icon={FiShield} />
                  </div>
                  <span className="text-white font-bold text-left">{p.prerequisite?.title}</span>
                </div>
                <Link to={`/courses/${p.prerequisite_id}`} className="text-indigo-400 text-xs font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  Access <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            ))}
          </div>
          <Link to="/courses" className="inline-block mt-12 text-slate-500 hover:text-white font-bold uppercase tracking-widest text-[10px] transition-colors">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto pb-20 px-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div className="flex items-center gap-4">
          <Link to="/courses" className="p-3 bg-slate-900 border border-slate-800 rounded-2xl text-slate-400 hover:text-white transition-all">
            <SafeIcon icon={FiChevronLeft} className="text-xl" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight uppercase">{course?.title}</h1>
            <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase mt-0.5">Strategic Learning Path</p>
          </div>
        </div>
        <div className="flex items-center gap-6 bg-slate-900 border border-slate-800 px-6 py-3 rounded-2xl">
          <div className="text-right">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Your Progress</p>
            <p className="text-lg font-bold text-indigo-400">{completedLessons.size} <span className="text-slate-600 text-sm">/ {totalLessons}</span></p>
          </div>
          <div className="w-48 h-3 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
            <motion.div 
              initial={{ width: 0 }} 
              animate={{ width: `${(completedLessons.size / (totalLessons || 1)) * 100}%` }} 
              className="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.4)]" 
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        <div className="xl:col-span-3 space-y-6">
          <div className="relative">
            {renderStage()}
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-8 flex flex-col md:flex-row justify-between items-center gap-6 shadow-xl">
            <div className="flex items-center gap-5 text-left w-full md:w-auto">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center text-xl shrink-0">
                <SafeIcon icon={FiTarget} />
              </div>
              <div>
                <h4 className="text-white font-bold text-xl">{activeItem?.title}</h4>
                <p className="text-[10px] text-slate-500 uppercase mt-1 font-bold tracking-widest">
                  {activeItem?.content_type} • Phase {modules.findIndex(m => m.id === activeItem?.module_id) + 1}
                </p>
              </div>
            </div>
            <button 
              onClick={handleComplete}
              disabled={completedLessons.has(activeItem?.id)}
              className={`w-full md:w-auto px-12 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 ${completedLessons.has(activeItem?.id) ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/20'}`}
            >
              <SafeIcon icon={completedLessons.has(activeItem?.id) ? FiCheckCircle : FiTarget} /> 
              {completedLessons.has(activeItem?.id) ? 'Lesson Completed' : 'Mark as Finished'}
            </button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 rounded-[32px] overflow-hidden shadow-2xl sticky top-28">
            <div className="p-8 border-b border-slate-800 bg-slate-800/30">
              <h3 className="font-bold text-white tracking-tight text-lg uppercase">Curriculum</h3>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">{modules.length} Strategic Phases</p>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
              {modules.map((module, mIdx) => (
                <div key={module.id} className="border-b border-slate-800/50">
                  <div className="px-8 py-3 bg-slate-800/10 text-[10px] font-bold text-slate-500 uppercase tracking-widest flex justify-between">
                    <span>Phase {mIdx + 1}</span>
                    <span className="text-slate-600">{module.lessons?.length || 0} Units</span>
                  </div>
                  <div className="divide-y divide-slate-800/30">
                    {module.lessons?.map((lesson) => {
                      const isActive = activeItem?.id === lesson.id;
                      const isComplete = completedLessons.has(lesson.id);
                      return (
                        <button 
                          key={lesson.id} 
                          onClick={() => setActiveItem({ ...lesson, type: 'lesson' })} 
                          className={`w-full px-8 py-5 flex items-start gap-4 transition-all text-left group ${isActive ? 'bg-indigo-600/10 border-l-4 border-l-indigo-500' : 'hover:bg-slate-800/50 border-l-4 border-l-transparent'}`}
                        >
                          <div className={`mt-1 shrink-0 ${isComplete ? 'text-emerald-400' : isActive ? 'text-indigo-400' : 'text-slate-600 group-hover:text-slate-400'}`}>
                            <SafeIcon icon={isComplete ? FiCheckCircle : lesson.content_type === 'video' ? FiPlay : FiFileText} />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <p className={`text-sm font-bold truncate ${isActive ? 'text-indigo-400' : 'text-slate-200 group-hover:text-white'}`}>{lesson.title}</p>
                            <p className="text-[9px] text-slate-500 uppercase mt-0.5 font-bold tracking-widest">{lesson.content_type}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}